import { getApiBaseUrl } from '../config/constants';
import { perfMark, perfTimeAsync } from '../utils/perfTimer';

export interface SiteConfig {
  tagline: string;
  section_heading: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
  meetings_enabled: boolean;
  /** Meeting code for /2026-workshop (e.g. SAA15). Set in Admin → Home. */
  workshop_2026_meeting_code?: string;
  /** When true + builder bypass email, SAA tab shows full meetings finder instead of 2026 card. */
  workshops_tab_show_full_finder?: boolean;
}

const DEFAULT_SITE_CONFIG: SiteConfig = {
  tagline: "Work your steps. Support your recovery. Join a step workshop.",
  section_heading: "What you'll find here",
  bullet1: "Step work that sticks with you — Getting Started, Steps 1–12, guided actions, videos, inventories, and progress that saves automatically when you're signed in.",
  bullet2: "2026 SAA Step Workshop — facilitator-led weekly online step work: interest list, confirm attendance, Zoom link, calendar, and workshop community tools when you're enrolled.",
  bullet3: "Privacy by design — your step work is encrypted; only you see it unless you choose to share.",
  meetings_enabled: true,
  workshop_2026_meeting_code: '',
  workshops_tab_show_full_finder: false,
};

/** Share one in-flight request; cleared when it settles (not a stale cache). */
let siteConfigInFlight: Promise<SiteConfig> | null = null;

async function fetchSiteConfigFromNetwork(): Promise<SiteConfig> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/site/config`, { cache: 'no-store' });
    if (!res.ok) return DEFAULT_SITE_CONFIG;
    const data = await res.json();
    return {
      tagline: data.tagline ?? DEFAULT_SITE_CONFIG.tagline,
      section_heading: data.section_heading ?? DEFAULT_SITE_CONFIG.section_heading,
      bullet1: data.bullet1 ?? DEFAULT_SITE_CONFIG.bullet1,
      bullet2: data.bullet2 ?? DEFAULT_SITE_CONFIG.bullet2,
      bullet3: data.bullet3 ?? DEFAULT_SITE_CONFIG.bullet3,
      meetings_enabled: data.meetings_enabled ?? DEFAULT_SITE_CONFIG.meetings_enabled,
      workshop_2026_meeting_code: data.workshop_2026_meeting_code ?? DEFAULT_SITE_CONFIG.workshop_2026_meeting_code,
      workshops_tab_show_full_finder: data.workshops_tab_show_full_finder ?? DEFAULT_SITE_CONFIG.workshops_tab_show_full_finder,
    };
  } catch {
    return DEFAULT_SITE_CONFIG;
  }
}

/** Public: get site config for home page (no auth). */
export async function getSiteConfig(): Promise<SiteConfig> {
  if (siteConfigInFlight) {
    perfMark('GET /site/config', 'joined in-flight request');
    return siteConfigInFlight;
  }

  siteConfigInFlight = perfTimeAsync('GET /site/config', fetchSiteConfigFromNetwork).finally(() => {
    siteConfigInFlight = null;
  });

  return siteConfigInFlight;
}

export { DEFAULT_SITE_CONFIG };
